/* LATTICE · pp-video — muted, looping, inline product videos + desktop card hover previews.
   Gallery/modal: <pp-video> (snippets/pp-video.liquid). Cards: [data-pp-card-video] (snippets/card-product.liquid).
   Rules: no video bytes before the frame is near the viewport; only the visible, active slide plays;
   prefers-reduced-motion / Save-Data / refused autoplay -> poster + pixel play button (tap = play muted, controls on). */
(() => {
  if (window.__ppVideo) return;
  window.__ppVideo = true;

  const rm = window.matchMedia('(prefers-reduced-motion: reduce)');
  const saveData = !!(navigator.connection && navigator.connection.saveData);
  const noAuto = () => false; // Kiandro 23:45: always autoplay muted, no manual mode
  const all = new Set();
  const evaluateAll = () => all.forEach((el) => el.evaluate());

  // Shared observers: "near" (attach sources) and "visible" (play/pause).
  const nearIO = new IntersectionObserver(
    (entries) => entries.forEach((e) => e.isIntersecting && e.target.__pp && e.target.__pp.near()),
    { rootMargin: '400px 0px' }
  );
  const visIO = new IntersectionObserver(
    (entries) =>
      entries.forEach((e) => {
        const el = e.target.__pp;
        if (!el) return;
        el.ratio = e.isIntersecting ? e.intersectionRatio : 0;
        el.visH = e.isIntersecting ? e.intersectionRect.height : 0;
        el.evaluate();
      }),
    { threshold: [0, 0.25, 0.5, 0.6, 0.75, 1] }
  );

  class PPVideo extends HTMLElement {
    connectedCallback() {
      if (this._init) {
        // re-inserted (e.g. Dawn reorders gallery slides on variant change): just observe again
        if (this.video) {
          all.add(this);
          nearIO.observe(this);
          visIO.observe(this);
          if (this.mo && this.slide) this.mo.observe(this.slide, { attributes: true, attributeFilter: ['class'] });
        }
        return;
      }
      this._init = true;
      this.video = this.querySelector('video');
      this.poster = this.querySelector('.pp-video__poster');
      this.playBtn = this.querySelector('.pp-video__play');
      this.toggleBtn = this.querySelector('.pp-video__toggle');
      this.ratio = 0;
      this.visH = 0;
      this.userPaused = false;
      this.userStarted = false;
      this.blocked = false;
      if (!this.video) return; // still processing: poster only
      this.__pp = this;
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.setAttribute('muted', '');
      all.add(this);
      nearIO.observe(this);
      visIO.observe(this);

      this.onPlaying = () => this.revealOnFirstFrame();
      this.video.addEventListener('playing', this.onPlaying);
      this.video.addEventListener('pause', () => {
        this.syncToggle();
        if (!this._selfPause && !this.video.controls) setTimeout(() => this.evaluate(), 80); // e.g. Dawn pauseAllMedia()
      });
      this.video.addEventListener('play', () => this.syncToggle());
      this.video.addEventListener('error', () => this.fail(), true);

      if (this.playBtn)
        this.playBtn.addEventListener('click', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          this.userStarted = true;
          this.userPaused = false;
          this.blocked = false;
          this.attach();
          this.video.controls = true; // controls only after an explicit tap (reduced motion / refused autoplay)
          this.video.removeAttribute('tabindex');
          this.playBtn.hidden = true;
          if (this.toggleBtn) this.toggleBtn.hidden = true;
          this.dataset.state = 'user';
          this.safePlay(true);
        });
      if (this.toggleBtn)
        this.toggleBtn.addEventListener('click', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          if (this.video.paused) {
            this.userPaused = false;
            this.evaluate(true);
          } else {
            this.userPaused = true;
            this.pause();
          }
        });

      this.slide = this.closest('.slider__slide, .product__media-item');
      if (this.slide) {
        this.mo = new MutationObserver(() => this.evaluate());
        this.mo.observe(this.slide, { attributes: true, attributeFilter: ['class'] });
      }
      if (this.hasAttribute('data-eager') && !noAuto()) this.attach(); // first slide: start fetching right away
      this.applyMode();
    }

    disconnectedCallback() {
      all.delete(this);
      nearIO.unobserve(this);
      visIO.unobserve(this);
      if (this.mo) this.mo.disconnect();
      if (this.video) this.video.pause();
    }

    applyMode() {
      if (!this.video || this.userStarted) return;
      const manual = noAuto() || this.blocked;
      if (this.playBtn) this.playBtn.hidden = !manual;
      if (this.toggleBtn) this.toggleBtn.hidden = manual;
      this.dataset.state = manual ? 'manual' : this.dataset.state === 'playing' ? 'playing' : 'idle';
      if (manual) this.pause();
      else this.evaluate();
    }

    near() {
      if (!noAuto()) this.attach();
    }

    attach() {
      if (this.attached || !this.video) return;
      this.attached = true;
      this.video.querySelectorAll('source[data-src]').forEach((s) => {
        s.src = s.dataset.src;
        s.removeAttribute('data-src');
      });
      this.video.preload = 'metadata';
      this.video.load();
    }

    isActive() {
      if (!this.offsetWidth) return false; // slide hidden (desktop thumbnail gallery shows only the active slide)
      if (this.dataset.context === 'modal') {
        const m = this.closest('product-modal');
        return !!(m && m.hasAttribute('open'));
      }
      // something modal is over the page (lightbox, drawer, menu): Dawn sets body.overflow-hidden
      if (document.body.classList.contains('overflow-hidden')) return false;
      return true;
    }

    shouldPlay() {
      return (
        !document.hidden && (this.ratio >= 0.6 || this.visH >= window.innerHeight * 0.5) && this.isActive() && !this.userPaused && !noAuto() && !this.blocked
      );
    }

    evaluate(force) {
      if (!this.video) return;
      if (this.userStarted) {
        if (this.ratio === 0 && !this.video.paused) this.pause(); // user-started: still pause off-screen, never auto-resume
        return;
      }
      if (force === true || this.shouldPlay()) {
        this.attach();
        this.safePlay(false);
      } else if (!this.video.paused) this.pause();
    }

    safePlay(fromUser) {
      this.video.muted = true;
      const p = this.video.play();
      if (p && p.catch)
        p.catch((err) => {
          if (err && err.name === 'NotAllowedError' && !fromUser) {
            // autoplay refused (e.g. iOS Low Power Mode): fall back to the tap-to-play poster
            this.blocked = false;
            // retry on the visitor's first touch/click anywhere (counts as a user gesture on iOS)
            const retry = () => {
              ['touchstart', 'pointerdown', 'keydown'].forEach((t) => document.removeEventListener(t, retry, true));
              this.blocked = false;
              this.applyMode();
              this.safePlay(true);
            };
            ['touchstart', 'pointerdown', 'keydown'].forEach((t) => document.addEventListener(t, retry, { capture: true, passive: true }));
          }
        });
    }

    pause() {
      this._selfPause = true;
      this.video.pause();
      setTimeout(() => (this._selfPause = false), 0);
    }

    revealOnFirstFrame() {
      const show = () => {
        this.dataset.state = this.userStarted ? 'user' : 'playing';
        this.classList.add('is-live');
      };
      if (this.classList.contains('is-live')) return show();
      if (this.video.requestVideoFrameCallback) this.video.requestVideoFrameCallback(show);
      else {
        const t = () => {
          if (this.video.currentTime > 0) {
            this.video.removeEventListener('timeupdate', t);
            show();
          }
        };
        this.video.addEventListener('timeupdate', t);
      }
    }

    syncToggle() {
      if (!this.toggleBtn) return;
      const paused = this.video.paused;
      this.toggleBtn.classList.toggle('is-paused', paused);
      this.toggleBtn.setAttribute('aria-label', paused ? this.toggleBtn.dataset.labelPlay : this.toggleBtn.dataset.labelPause);
    }

    fail() {
      // sources missing/unplayable: keep the poster, drop the player UI
      if (this.video.error || this.video.networkState === 3) {
        this.dataset.state = 'error';
        if (this.toggleBtn) this.toggleBtn.hidden = true;
        if (this.playBtn) this.playBtn.hidden = true;
      }
    }
  }
  if (!customElements.get('pp-video')) customElements.define('pp-video', PPVideo);

  document.addEventListener('visibilitychange', evaluateAll);
  new MutationObserver(evaluateAll).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  const onRM = () => all.forEach((el) => el.applyMode());
  rm.addEventListener ? rm.addEventListener('change', onRM) : rm.addListener(onRM);

  /* ---------- Collection cards: muted hover preview, desktop fine pointers only ---------- */
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const cardOK = () => fine.matches && !noAuto();
  const enter = (card) => {
    if (!cardOK()) return;
    clearTimeout(card.__ppT);
    card.__ppT = setTimeout(() => {
      let v = card.__ppV;
      if (!v) {
        const host = card.querySelector('.card__media .media');
        if (!host) return;
        v = document.createElement('video');
        v.className = 'pp-card-video';
        v.muted = true;
        v.defaultMuted = true;
        v.loop = true;
        v.playsInline = true;
        v.setAttribute('muted', '');
        v.setAttribute('playsinline', '');
        v.setAttribute('aria-hidden', 'true');
        v.setAttribute('tabindex', '-1');
        v.disablePictureInPicture = true;
        v.preload = 'auto';
        v.src = card.dataset.ppCardVideo; // first hover only
        v.addEventListener('playing', () => card.__ppHover && card.classList.add('pp-card-video-on'));
        host.appendChild(v);
        card.__ppV = v;
      }
      card.__ppHover = true;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    }, 140);
  };
  const leave = (card) => {
    clearTimeout(card.__ppT);
    card.__ppHover = false;
    card.classList.remove('pp-card-video-on');
    if (card.__ppV) card.__ppV.pause();
  };
  document.addEventListener(
    'pointerover',
    (e) => {
      if (e.pointerType !== 'mouse') return;
      const card = e.target.closest && e.target.closest('[data-pp-card-video]');
      if (card && !(e.relatedTarget && card.contains(e.relatedTarget))) enter(card);
    },
    { passive: true }
  );
  document.addEventListener(
    'pointerout',
    (e) => {
      const card = e.target.closest && e.target.closest('[data-pp-card-video]');
      if (card && !(e.relatedTarget && card.contains(e.relatedTarget))) leave(card);
    },
    { passive: true }
  );
})();
